import { NextPageWithLayout } from "@bptypes/layout";
import { TitleControls } from "@components/headings";
import { ReleasesList } from "@components/lists";
import { ReleasesTable } from "@components/tables/ReleasesTable";
import MainLayout from "@layouts/MainLayout";
import { DEFAULT_LOCALE } from "@lib/constants";
import { exportAnonSession, useSessionContext } from "@lib/context/session";
import { useMediaQuery } from "@lib/hooks/useMediaQuery";
import { getGenreQuery } from "@lib/network/genres";
import { getGenreTopReleasesQuery } from "@lib/network/releases";
import { getLocationData } from "@lib/utils/getLocationData";
import { Genre } from "@models/Genre";
import { device } from "@styles/theme";
import { DehydratedState, QueryClient, dehydrate, useQuery } from "@tanstack/react-query";
import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import { i18n } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { ReactElement } from "react";

const TOTAL_RELEASES = 100;
const TITLE = "Top 100 Releases";

interface Props {
	genre?: Genre;
	anonSession: AnonSession;
}

const Top100Release: NextPageWithLayout<Props> = ({ genre, anonSession }) => {
	const isDesktop = useMediaQuery({ query: device.xl });
	const { getAccessToken, getIsSessionValid, importAnonSession } = useSessionContext();
	importAnonSession(anonSession);
	const accessToken = getAccessToken();
	const isSessionValid = getIsSessionValid();
	const router = useRouter();

	const { id } = router.query;
	const genreId = id ? parseInt(id as string, 10) : 0;

	if (genreId === 0) {
		router.push("/");
	}

	const releasesQuery = useQuery(
		{
			...getGenreTopReleasesQuery({ accessToken, genreId, number: TOTAL_RELEASES }),
			enabled: isSessionValid,
		},
	);

	return (
		<>
			<TitleControls preText={genre?.name} title={TITLE} />
			{isDesktop ?
					(
						<ReleasesTable
							location={`Genre Page - ${genre?.name} - ${TITLE}`}
							showNumbers={true}
							isLoading={releasesQuery.isLoading}
							releases={releasesQuery.data ? releasesQuery.data.results : []}
						/>
					) :
					(
						<ReleasesList
							location={`Genre Page - ${genre?.name} - ${TITLE}`}
							releases={releasesQuery.data ? releasesQuery.data.results : []}
							showArtwork
							showNumbers
						/>
					)}
		</>
	);
};

export default Top100Release;

Top100Release.getLayout = (page: ReactElement<Props>) => {
	return (
		<MainLayout
			title={`${page.props.genre?.name} ${i18n?.t("Top.Top100Songs")}`}
			metaDescription={i18n?.t("Top.CheckOutTop100")}
		>
			{page}
		</MainLayout>
	);
};

type ServerSideProps = {
	dehydratedState: DehydratedState;
	anonSession: AnonSession;
	genre: Genre | null;
};

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async (
	ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<ServerSideProps>> => {
	const queryClient = new QueryClient();

	const { id } = ctx.params as { id: string };
	const location = getLocationData(ctx.req);
	const genreId = id ? parseInt(id as string, 10) : 0;
	let genre: Genre | null = null;

	if (genreId > 0) {
		await Promise.all([
			queryClient
				.fetchQuery(getGenreQuery({ genreId }))
				.then((res) => res)
				.catch(() => { }),
			queryClient
				.fetchQuery(getGenreTopReleasesQuery({ genreId, number: TOTAL_RELEASES, location }))
				.then((res) => res)
				.catch(() => { }),
		]).then((values) => {
			// assign genre
			genre = values[0];
		});
	}

	const anonSession = await exportAnonSession();

	return {
		notFound: !genre,
		props: {
			dehydratedState: dehydrate(queryClient),
			anonSession,
			...(await serverSideTranslations(ctx.locale ?? DEFAULT_LOCALE, ["translation"])),
			genre: genre,
		},
	};
};
